import React, { useState, useEffect } from "react";
import { TextField, Box, IconButton, CircularProgress } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { ArrowBack, ArrowForward } from "@mui/icons-material";

export default function MetData() {
  const [date, setDate] = useState(dayjs().subtract(2, "day"));
  const [reportUrl, setReportUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  // Debounced fetch whenever date changes
  useEffect(() => {
    if (!date) return;

    setLoading(true);

    const timer = setTimeout(() => {
      const archive_id = date.format("YYYYMMDD");
      const host = window.location.protocol + "//" + window.location.hostname;

      const query_date = host + process.env.REACT_APP_API_GET_METDATA;
      const query_report = host + process.env.REACT_APP_API_GET_METDATA_REPORT;

      fetch(`${query_date}/${archive_id}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.html_page) {
            setReportUrl(`${query_report}/${data.html_page}`);
          } else {
            setReportUrl(null);
          }
        })
        .catch(() => setReportUrl(null))
        .finally(() => setLoading(false));
    }, 300);

    return () => clearTimeout(timer);
  }, [date]);

  // Handlers for prev/next arrows
  const handlePrev = () => setDate((prev) => prev.subtract(1, "day"));
  const handleNext = () => setDate((prev) => prev.add(1, "day"));

  // Keyboard navigation: left/right arrows
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") {
        handlePrev();
      } else if (event.key === "ArrowRight") {
        handleNext();
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box p={2} display="flex" flexDirection="column" alignItems="center">
        {/* Centered date picker with arrows */}
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={handlePrev}>
            <ArrowBack />
          </IconButton>

          <DatePicker
            label="Select Date"
            value={date}
            onChange={(newDate) => newDate && setDate(newDate)}
            format="YYYY/MM/DD"
            renderInput={(params) => <TextField {...params} />}
          />

          <IconButton onClick={handleNext}>
            <ArrowForward />
          </IconButton>
        </Box>

        {/* Iframe or placeholder */}
        <Box sx={{ width: "100%", height: "80vh", position: "relative" }}>
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255,255,255,0.6)",
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {reportUrl ? (
            <iframe
              src={reportUrl}
              title="Meteorological Data"
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          ) : (
            !loading && <Box>No report available for this date.</Box>
          )}
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
